import React, { useState, useEffect, useMemo } from "react";
import { ClipboardList, UserPlus, Trash2, Check, Clock, X, Printer, Phone } from "lucide-react";
import { useMember } from "../context/MemberContext";
import { fetchToolData, saveToolData, davetliKey } from "../lib/toolCloud";

const uid = () => Math.random().toString(36).slice(2, 9);

const SIDES = [
  { key: "gelin", label: "Gelin yakını" },
  { key: "damat", label: "Damat yakını" },
];

const STATUS = {
  onayli: { label: "Onaylı", cls: "bg-[#E6F4EA] text-[#2f7d4f]", icon: Check },
  bekliyor: { label: "Bekliyor", cls: "bg-[#FBF3E4] text-[#8a6d1f]", icon: Clock },
  gelmeyecek: { label: "Gelmeyecek", cls: "bg-[#FBEAEA] text-[#C0392B]", icon: X },
};

const sideLabel = (k) => SIDES.find((s) => s.key === k)?.label || "";

const GuestListTool = () => {
  const { token, member } = useMember();
  const mid = member?.id;
  const [guests, setGuests] = useState([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [side, setSide] = useState("gelin");
  const [count, setCount] = useState(1);
  const [filter, setFilter] = useState("all");
  const [cloudReady, setCloudReady] = useState(false);

  useEffect(() => {
    let active = true;
    setCloudReady(false);
    (async () => {
      let d = await fetchToolData("davetli", token);
      if (!d && mid) {
        try { const raw = localStorage.getItem(davetliKey(mid)); if (raw) d = JSON.parse(raw); } catch {}
      }
      if (active) {
        const loaded = Array.isArray(d?.guests) ? d.guests : [];
        setGuests((prev) => [...loaded, ...prev.filter((g) => !loaded.some((l) => l.id === g.id))]);
        setCloudReady(true);
      }
    })();
    return () => { active = false; };
  }, [token, mid]);

  useEffect(() => {
    if (!cloudReady || !mid) return;
    localStorage.setItem(davetliKey(mid), JSON.stringify({ guests }));
    if (token) saveToolData("davetli", token, { guests });
  }, [guests, cloudReady, token, mid]);

  const add = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setGuests((g) => [...g, { id: uid(), name: name.trim(), phone: phone.trim(), side, count: Number(count) || 1, status: "bekliyor" }]);
    setName("");
    setPhone("");
    setCount(1);
  };
  const update = (id, field, value) => setGuests((g) => g.map((x) => (x.id === id ? { ...x, [field]: value } : x)));
  const remove = (id) => setGuests((g) => g.filter((x) => x.id !== id));

  const totals = useMemo(() => {
    const t = { people: 0, onayli: 0, bekliyor: 0, gelmeyecek: 0 };
    guests.forEach((g) => {
      const c = Number(g.count) || 1;
      t.people += c;
      t[g.status] = (t[g.status] || 0) + c;
    });
    return t;
  }, [guests]);

  const shown = filter === "all" ? guests : guests.filter((g) => g.status === filter);

  const esc = (s) => String(s || "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const exportPdf = () => {
    const today = new Date().toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
    const rows = guests
      .map((g) => `<tr><td>${esc(g.name)}</td><td>${esc(sideLabel(g.side))}</td><td>${esc(g.phone) || "-"}</td><td class="num">${Number(g.count) || 1}</td><td>${STATUS[g.status]?.label || ""}</td></tr>`)
      .join("");
    const html = `<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>Davetli Listesi</title>
<style>
  *{box-sizing:border-box;font-family:'Segoe UI',Arial,sans-serif}
  body{margin:0;padding:40px;color:#2A2224}
  .head{border-bottom:3px solid #9E2A3B;padding-bottom:16px;margin-bottom:24px}
  h1{color:#9E2A3B;margin:0 0 4px;font-size:24px}
  .sub{color:#7C6E70;font-size:13px}
  .cards{display:flex;gap:12px;margin-bottom:24px;flex-wrap:wrap}
  .card{flex:1;min-width:120px;border:1px solid #F0DDE0;border-radius:12px;padding:14px;background:#FBF5F0}
  .card .l{font-size:12px;color:#7C6E70}
  .card .v{font-size:20px;font-weight:700;margin-top:4px}
  .card.main{background:#9E2A3B;color:#fff;border:none}
  .card.main .l{color:rgba(255,255,255,.8)}
  table{width:100%;border-collapse:collapse;font-size:14px}
  th,td{text-align:left;padding:9px 12px;border-bottom:1px solid #F0DDE0}
  th{color:#7C6E70;font-weight:600;font-size:12px;text-transform:uppercase}
  td.num,th.num{text-align:right}
  .foot{margin-top:30px;color:#9c8f90;font-size:11px;text-align:center}
</style></head><body>
  <div class="head"><h1>Davetli Listesi</h1><div class="sub">DüğünSenin.com · ${today}</div></div>
  <div class="cards">
    <div class="card main"><div class="l">Toplam Kişi</div><div class="v">${totals.people}</div></div>
    <div class="card"><div class="l">Onaylı</div><div class="v">${totals.onayli}</div></div>
    <div class="card"><div class="l">Bekleyen</div><div class="v">${totals.bekliyor}</div></div>
    <div class="card"><div class="l">Gelmeyecek</div><div class="v">${totals.gelmeyecek}</div></div>
  </div>
  <table>
    <thead><tr><th>Ad Soyad</th><th>Yakınlık</th><th>Telefon</th><th class="num">Kişi</th><th>Durum</th></tr></thead>
    <tbody>${rows || '<tr><td colspan="5">Davetli yok</td></tr>'}</tbody>
  </table>
  <div class="foot">Bu liste DüğünSenin.com Davetli Listem aracı ile oluşturulmuştur.</div>
  <script>window.onload=function(){window.print()}<\/script>
</body></html>`;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
  };

  return (
    <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6 sm:p-8" data-testid="guestlist-tool">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#C15B6B] to-[#9E2A3B] flex items-center justify-center">
            <ClipboardList className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-[#2A2224]">Davetli Listem</h1>
            <p className="text-sm text-[#7C6E70]">Davetlileri, telefon ve katılım durumlarını takip edin</p>
          </div>
        </div>
        <button onClick={exportPdf} data-testid="guest-pdf-btn" className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#9E2A3B] text-[#9E2A3B] text-sm font-medium hover:bg-[#9E2A3B] hover:text-white transition-colors shrink-0">
          <Printer className="w-4 h-4" /> <span className="hidden sm:inline">PDF / Yazdır</span>
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <SummaryCard label="Toplam Kişi" value={totals.people} testid="guest-total-people" main />
        <SummaryCard label="Onaylı" value={totals.onayli} testid="guest-total-onayli" cls="text-[#2f7d4f]" />
        <SummaryCard label="Bekleyen" value={totals.bekliyor} testid="guest-total-bekliyor" cls="text-[#8a6d1f]" />
        <SummaryCard label="Gelmeyecek" value={totals.gelmeyecek} testid="guest-total-gelmeyecek" cls="text-[#C0392B]" />
      </div>

      {/* Add form */}
      <form onSubmit={add} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_140px_90px_auto] gap-2 mb-5">
        <input
          data-testid="guest-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ad Soyad..."
          className="px-3 py-2.5 rounded-xl border border-[#F0DDE0] text-sm focus:outline-none focus:border-[#9E2A3B]"
        />
        <input
          data-testid="guest-phone-input"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Telefon (opsiyonel)"
          className="px-3 py-2.5 rounded-xl border border-[#F0DDE0] text-sm focus:outline-none focus:border-[#9E2A3B]"
        />
        <select data-testid="guest-side-select" value={side} onChange={(e) => setSide(e.target.value)} className="px-3 py-2.5 rounded-xl border border-[#F0DDE0] text-sm bg-white focus:outline-none focus:border-[#9E2A3B]">
          {SIDES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <input
          data-testid="guest-count-input"
          type="number"
          min="1"
          value={count}
          onChange={(e) => setCount(e.target.value)}
          title="Kişi sayısı"
          className="px-3 py-2.5 rounded-xl border border-[#F0DDE0] text-sm focus:outline-none focus:border-[#9E2A3B]"
        />
        <button data-testid="guest-add-btn" type="submit" className="flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl bg-[#9E2A3B] text-white text-sm font-medium hover:bg-[#861f2f] transition-colors">
          <UserPlus className="w-4 h-4" /> Ekle
        </button>
      </form>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        {[["all", "Tümü"], ["onayli", "Onaylı"], ["bekliyor", "Bekleyen"], ["gelmeyecek", "Gelmeyecek"]].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            data-testid={`guest-filter-${k}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === k ? "bg-[#9E2A3B] text-white" : "bg-[#FBF5F0] text-[#7C6E70] hover:bg-[#F6E4E6]"}`}
          >
            {l}
          </button>
        ))}
      </div>

      {/* List */}
      <ul className="divide-y divide-[#F6E4E6]" data-testid="guestlist-list">
        {shown.map((g) => (
          <li key={g.id} className="flex flex-wrap items-center gap-3 py-3 group">
            <div className="flex-1 min-w-[130px]">
              <p className="text-sm font-medium text-[#2A2224]">{g.name}</p>
              <p className="text-xs text-[#9c8f90] flex items-center gap-1.5 flex-wrap">
                <span>{sideLabel(g.side)} · {g.count} kişi</span>
                {g.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {g.phone}</span>}
              </p>
            </div>
            <div className="flex gap-1">
              {Object.entries(STATUS).map(([k, v]) => {
                const I = v.icon;
                const on = g.status === k;
                return (
                  <button
                    key={k}
                    onClick={() => update(g.id, "status", k)}
                    data-testid={`guest-status-${g.id}-${k}`}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${on ? v.cls + " ring-1 ring-current" : "bg-[#FBF5F0] text-[#b6a9ab] hover:bg-[#F6E4E6]"}`}
                  >
                    <I className="w-3 h-3" /> {v.label}
                  </button>
                );
              })}
            </div>
            <button onClick={() => remove(g.id)} data-testid={`guest-remove-${g.id}`} className="text-[#c9a0a6] hover:text-[#9E2A3B] opacity-0 group-hover:opacity-100 transition-opacity">
              <Trash2 className="w-4 h-4" />
            </button>
          </li>
        ))}
        {shown.length === 0 && <li className="py-6 text-center text-sm text-[#9c8f90]">Bu listede davetli yok.</li>}
      </ul>
    </div>
  );
};

const SummaryCard = ({ label, value, testid, cls, main }) => (
  <div className={`rounded-xl p-4 ${main ? "bg-gradient-to-br from-[#C15B6B] to-[#9E2A3B] text-white" : "border border-[#F0DDE0] bg-[#FBF5F0]"}`}>
    <p className={`text-xs ${main ? "text-white/80" : "text-[#7C6E70]"}`}>{label}</p>
    <p data-testid={testid} className={`text-2xl font-bold ${main ? "text-white" : cls || "text-[#2A2224]"}`}>{value}</p>
  </div>
);

export default GuestListTool;
